// Every write goes through an operation function (architecture §3.3, §6). One command id per user action:
// a retry after a lost response returns the first result instead of posting twice.
import { reportCommand } from '../lib/ux/feedback';
import { Refusal } from './auth';
import { supabase } from './supabase';

export function newCommandId(): string {
  return crypto.randomUUID();
}

/** Calls api_v1.<operation>(p_command_id, ...params). Refusals carry the database's stable code. */
export async function sendCommand<T = unknown>(
  operation: string,
  params: Record<string, unknown>,
  commandId: string = newCommandId(),
  /** Offline-capable callers handle OFFLINE themselves (they queue the command): no error toast for it. */
  quiet = false,
): Promise<T> {
  let result: { data: unknown; error: { message: string; details?: string | null } | null };
  try {
    result = await supabase.rpc(operation, { p_command_id: commandId, ...params });
  } catch {
    if (!quiet) reportCommand({ ok: false, operation, code: 'OFFLINE' });
    throw new Refusal('OFFLINE');
  }
  if (result.error) {
    const code = /^[A-Z_]+$/.test(result.error.message)
      ? result.error.message
      : /fetch/i.test(result.error.message)
        ? 'OFFLINE'
        : 'SERVER_ERROR';
    if (!(quiet && code === 'OFFLINE')) reportCommand({ ok: false, operation, code });
    throw new Refusal(code, parseDetail(result.error.details));
  }
  reportCommand({ ok: true, operation, result: result.data });
  return result.data as T;
}

function parseDetail(details: string | null | undefined): Record<string, unknown> {
  if (!details) return {};
  try {
    const value: unknown = JSON.parse(details);
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
